import { Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import { IPrikazPrevod, PrikazPrevodGet, PrikazPrevodPut } from 'services/dokumenty/prikazy';
import { ISmlouvniStrany } from 'services/dokumenty/smlouva';

import { isIsinDlt } from '../NovyDokument';
import { getDataIsins } from './Zruseni';

interface IPrikazPrevodComponent {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const PrikazPrevod: React.FC<IPrikazPrevodComponent> = ({ nextStep, zadostID, prikazyData }) => {
    const { dokumenty } = useContext(Context);
    const [form] = Form.useForm();
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [data, setData] = useState<IPrikazPrevod>();
    const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        getDataIsins(dokumenty).map(e => {
            return {
                labelcz: e.isin,
                value: e.isin
            };
        }) || []
    );

    const [selectedIsin, setSelectIsin] = useState<string>();
    const [isDLT, setIsDLT] = useState<boolean>();
    useEffect(() => {
        if (selectedIsin) {
            setIsDLT(isIsinDlt(selectedIsin, dokumenty!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsin]);

    useEffect(() => {
        setLoading(true);
        PrikazPrevodGet(zadostID)
            .then(resp => {
                setSelectIsin(resp.isin);
                setData(resp);
                form.setFieldsValue({
                    typ: resp.typ
                });
            })
            .catch(() => setData({} as IPrikazPrevod))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {data && prikazyData && (
                <>
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            setLoading(true);
                            const dataToSend = {
                                ...(values as any),
                                datum: values.datum.format('YYYY-MM-DD')
                            };
                            delete dataToSend.idmbSpolecnost;
                            delete dataToSend.dicdphSpolecnost;
                            delete dataToSend.nazevSpolecnost;
                            delete dataToSend.leiSpolecnost;
                            PrikazPrevodPut(zadostID, dataToSend)
                                .then(() => nextStep())
                                .finally(() => setLoading(false));
                        }}
                    >
                        <Title text="EMITENT" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        {prikazyData?.spolecnost.dicdph && (
                            <InputComponent
                                disabled={true}
                                initialValue={prikazyData?.spolecnost.dicdph}
                                label="ISIN_IDENTIFIKACE_DIC"
                                name="dicdphSpolecnost"
                                notRequired={true}
                            />
                        )}
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.nazev}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazevSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.lei}
                            label="LEI_CELE"
                            name="leiSpolecnost"
                            notRequired={true}
                        />
                        <SelectComponent
                            name="isin"
                            labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                            defaultValue={data?.isin}
                            data={dataIsins}
                            isFormItem={true}
                            onChange={val => setSelectIsin(val)}
                        />
                        <SelectComponent
                            name="typ"
                            labelParagraph={'PRIKAZ_PREVOD_TYP'}
                            defaultValue={data?.typ}
                            data={[
                                {
                                    labelcz: 'PRIKAZ_PREVOD_TYP_ZAPIS',
                                    value: 'ZAPIS'
                                },
                                {
                                    labelcz: 'PRIKAZ_PREVOD_TYP_ZRUSENI',
                                    value: 'ZRUSENI'
                                }
                            ]}
                            isFormItem={true}
                            onChange={() => rerender(pv => !pv)}
                        />
                        {form.getFieldValue('typ') === 'ZAPIS' && (
                            <InputTextArea
                                rows={4}
                                name="popisomezeni"
                                initialValue={data?.popisomezeni}
                                label="PRIKAZ_PREVOD_TYP_POPIS"
                            />
                        )}
                        <DatePickerComponent
                            name="datum"
                            initialValue={data?.datum ? dayjs(data.datum) : undefined}
                            // disabledDate={current => current < dayjs().add(3, 'days')}
                            label="PRIKAZ_PREVOD_DATUM"
                        />
                        <PrilohyTable zadostID={zadostID} required={true} />
                        <Title level={5} text="PRIKAZ_FORMA_PODMINKY_TITLE" />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_PREVOD_PODMINKY_DLT_TEXT1' : 'PRIKAZ_PREVOD_PODMINKY_TEXT1'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Paragraph
                            text={'PRIKAZ_PREVOD_PODMINKY_TEXT2'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Row
                            style={{
                                marginTop: 26
                            }}
                        >
                            <CheckboxComponent
                                name="souhlas"
                                checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                                onChange={() => setIsChecked(pv => !pv)}
                                checked={isChecked}
                            />
                        </Row>
                        <Row className="button-row">
                            <Col>
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    id="isinPokracovatBtn"
                                    disabled={!isChecked}
                                    htmlType="submit"
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                </>
            )}
        </LoadingWrapper>
    );
};

export default PrikazPrevod;
